import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import {
  CpuChipIcon,
  BoltIcon,
  ShieldCheckIcon,
  DocumentDuplicateIcon,
  CheckCircleIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';

const AIInvoiceProcessing: React.FC = () => {
  const capabilities = [
    {
      icon: CpuChipIcon,
      title: 'Multi-Format Invoice Recognition',
      description: 'Process PDF, JPG, PNG, scanned documents, WhatsApp images - any invoice format with 99.5% accuracy',
    },
    {
      icon: DocumentDuplicateIcon,
      title: 'Auto-Extract GST Fields',
      description: 'Automatically extract GSTIN, invoice number, date, HSN codes, tax amounts, and line items',
    },
    {
      icon: BoltIcon,
      title: 'Bulk Invoice Processing',
      description: 'Upload and process hundreds of GST invoices simultaneously. Save 20+ hours per week',
    },
    {
      icon: ShieldCheckIcon,
      title: 'Smart Validation & Verification',
      description: 'AI validates GSTIN format, HSN codes, tax calculations and flags errors automatically',
    },
  ];

  const workflow = [
    {
      step: 'Upload',
      description: 'Clients upload invoices via WhatsApp, email, or client portal',
    },
    {
      step: 'AI Extract',
      description: 'AI scans and extracts all GST fields in under 5 seconds per invoice',
    },
    {
      step: 'Validate',
      description: 'Auto-validate GSTIN, HSN codes, tax rates for compliance',
    },
    {
      step: 'Export',
      description: 'Export to Excel, Tally, or generate GSTR-1/3B directly',
    },
  ];

  const benefits = [
    '99.5% accuracy on GST invoice data extraction',
    'Process 100+ invoices in minutes vs days of manual entry',
    'Reduce GST filing errors by 95%',
    'Support for Hindi, English, regional language invoices',
    'Auto-categorize input/output GST for reconciliation',
    'Direct integration with Tally, QuickBooks, Excel',
  ];

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'AI Invoice Processing for GST',
    provider: {
      '@type': 'Organization',
      name: 'AICaFriend',
    },
    description: 'AI-powered invoice processing service for GST compliance in India. Automate invoice data extraction, validation, and GSTR filing.',
    areaServed: 'IN',
  };

  return (
    <>
      <SEO
        title="AI Invoice Processing for GST - Automate Invoice Data Extraction"
        description="AI-powered invoice processing for GST compliance. Auto-extract GSTIN, HSN codes, tax amounts from any invoice format. 99.5% accuracy. Process 100+ invoices in minutes. Free trial."
        keywords="ai invoice processing, gst invoice scanner, invoice data extraction, automated invoice processing, ai ocr invoice, gst invoice automation, invoice recognition software"
        canonicalUrl="https://aicafriend.com/ai-invoice-processing"
        structuredData={structuredData}
      />

      <div className="bg-white">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-purple-50 via-white to-indigo-50 overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
            <div className="text-center">
              <div className="inline-flex items-center bg-purple-100 text-purple-800 px-4 py-2 rounded-full mb-6">
                <CpuChipIcon className="h-5 w-5 mr-2" />
                <span className="font-semibold">99.5% AI Accuracy on GST Invoices</span>
              </div>

              <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
                AI Invoice Processing for
                <span className="gradient-text"> GST Compliance</span>
              </h1>

              <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-4xl mx-auto">
                Automate GST invoice data extraction with AI. Process PDF, scanned, or image invoices instantly.
                Extract GSTIN, HSN codes, tax amounts with 99.5% accuracy. Save 20+ hours per week on data entry.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                <Link
                  to="/pricing"
                  className="btn-primary px-8 py-4 text-lg font-semibold rounded-lg text-white shadow-lg"
                >
                  Try AI Invoice Scanner Free
                </Link>
                <Link
                  to="/features"
                  className="btn-secondary px-8 py-4 text-lg font-semibold rounded-lg"
                >
                  Watch Demo Video
                </Link>
              </div>

              <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-600">
                <div className="flex items-center">
                  <CheckCircleIcon className="h-5 w-5 text-purple-500 mr-2" />
                  99.5% extraction accuracy
                </div>
                <div className="flex items-center">
                  <ClockIcon className="h-5 w-5 text-purple-500 mr-2" />
                  5 seconds per invoice
                </div>
                <div className="flex items-center">
                  <CheckCircleIcon className="h-5 w-5 text-purple-500 mr-2" />
                  All invoice formats supported
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Capabilities Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Advanced AI Invoice Processing Capabilities
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Our AI processes any invoice format and extracts all GST fields automatically
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {capabilities.map((capability, index) => (
                <div
                  key={index}
                  className="card-hover bg-white p-8 rounded-xl border border-gray-200"
                >
                  <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-indigo-500 rounded-lg flex items-center justify-center mb-4">
                    <capability.icon className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{capability.title}</h3>
                  <p className="text-gray-600">{capability.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Workflow Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                How AI Invoice Processing Works
              </h2>
              <p className="text-xl text-gray-600">
                From invoice upload to GST return in 4 automated steps
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {workflow.map((item, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                    {index + 1}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{item.step}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="bg-gradient-to-r from-purple-500 to-indigo-600 rounded-2xl p-8 text-white">
                <h3 className="text-2xl font-bold mb-6">What AI Extracts from Invoices</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex items-center">
                    <CheckCircleIcon className="h-5 w-5 mr-3 flex-shrink-0" />
                    <span>Supplier & Customer GSTIN</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircleIcon className="h-5 w-5 mr-3 flex-shrink-0" />
                    <span>Invoice Number & Date</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircleIcon className="h-5 w-5 mr-3 flex-shrink-0" />
                    <span>HSN/SAC Codes</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircleIcon className="h-5 w-5 mr-3 flex-shrink-0" />
                    <span>Taxable Value</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircleIcon className="h-5 w-5 mr-3 flex-shrink-0" />
                    <span>CGST, SGST, IGST Amounts</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircleIcon className="h-5 w-5 mr-3 flex-shrink-0" />
                    <span>Total Invoice Amount</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircleIcon className="h-5 w-5 mr-3 flex-shrink-0" />
                    <span>Line Items with Quantities & Rates</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircleIcon className="h-5 w-5 mr-3 flex-shrink-0" />
                    <span>Place of Supply</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircleIcon className="h-5 w-5 mr-3 flex-shrink-0" />
                    <span>Reverse Charge Applicability</span>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                  Why CA Firms Choose Our AI Invoice Processing
                </h2>
                <div className="space-y-4">
                  {benefits.map((benefit, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <CheckCircleIcon className="h-6 w-6 text-purple-500 flex-shrink-0 mt-1" />
                      <span className="text-gray-700 text-lg">{benefit}</span>
                    </div>
                  ))}
                </div>

                <Link
                  to="/pricing"
                  className="mt-8 inline-block bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-8 py-3 rounded-lg font-semibold hover:from-purple-700 hover:to-indigo-700 transition-all duration-200"
                >
                  Start Free Trial
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Use Cases Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Perfect for All CA Practice Scenarios
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white p-6 rounded-xl border border-gray-200">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Monthly GST Filing</h3>
                <p className="text-gray-600">
                  Process hundreds of client invoices monthly. Auto-generate GSTR-1 and GSTR-3B returns ready to file.
                </p>
              </div>
              <div className="bg-white p-6 rounded-xl border border-gray-200">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">GST Audit & Reconciliation</h3>
                <p className="text-gray-600">
                  Extract and reconcile years of historical invoices for GST audits. Identify mismatches automatically.
                </p>
              </div>
              <div className="bg-white p-6 rounded-xl border border-gray-200">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Input Credit Claim</h3>
                <p className="text-gray-600">
                  Auto-categorize input invoices. Calculate available ITC. Ensure maximum credit utilization.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-purple-600 to-indigo-600">
          <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Start Processing Invoices with AI Today
            </h2>
            <p className="text-xl text-purple-100 mb-8">
              Join 500+ CA firms saving 20+ hours per week on invoice data entry with our AI invoice processing
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/pricing"
                className="bg-white text-purple-600 px-8 py-4 text-lg font-semibold rounded-lg hover:bg-gray-50 transform hover:scale-105 transition-all duration-200"
              >
                Get Started Free
              </Link>
              <Link
                to="/contact"
                className="border-2 border-white text-white px-8 py-4 text-lg font-semibold rounded-lg hover:bg-white hover:text-purple-600 transition-all duration-200"
              >
                Request Demo
              </Link>
            </div>
            <p className="mt-6 text-sm text-purple-100">
              Free trial • No credit card required • 99.5% accuracy guarantee
            </p>
          </div>
        </section>

        {/* Related Links Section */}
        <section className="py-12 bg-gray-50 border-t border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Related GST Solutions</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Link
                to="/free-gst-software"
                className="p-6 bg-white rounded-lg border border-gray-200 hover:shadow-lg transition-all duration-200"
              >
                <h4 className="font-semibold text-gray-900 mb-2">Free GST Software</h4>
                <p className="text-gray-600 text-sm">Discover our free forever plan for CA firms</p>
              </Link>
              <Link
                to="/gst-automation-for-ca"
                className="p-6 bg-white rounded-lg border border-gray-200 hover:shadow-lg transition-all duration-200"
              >
                <h4 className="font-semibold text-gray-900 mb-2">Complete GST Automation</h4>
                <p className="text-gray-600 text-sm">End-to-end GST workflow automation for CAs</p>
              </Link>
              <Link
                to="/resources"
                className="p-6 bg-white rounded-lg border border-gray-200 hover:shadow-lg transition-all duration-200"
              >
                <h4 className="font-semibold text-gray-900 mb-2">GST Resources & Guides</h4>
                <p className="text-gray-600 text-sm">Free downloadable GST checklists and templates</p>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default AIInvoiceProcessing;
